import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaGlobe,
  FaBullhorn,
  FaYoutube,
  FaSave,
  FaImage,
  FaUpload,
  FaTrash,
  FaEye,
  FaLink,
  FaPalette,
  FaPlay,
  FaCheckCircle,
  FaExclamationCircle,
  FaMagic,
  FaMobileAlt,
  FaMousePointer,
  FaFont,
  FaAlignLeft,
} from 'react-icons/fa';
import { apiHelper } from '../../utils/api';
import { showToast } from '../../utils/toast';
import './Admin.scss';

const DEFAULT_SETTINGS = {
  adEnabled: false,
  adType: 'text',           // 'media' | 'text'
  adTitle: '',
  adDescription: '',
  adMediaUrl: '',
  adMediaType: '',          // 'image' | 'video'
  adButtonText: 'Learn More',
  adButtonUrl: '',
  youtubeUrl: '',
  bannerEnabled: false,
  bannerText: '',
  bannerLink: '',
  bannerBgColor: '#6366f1',
  bannerTextColor: '#ffffff',
};

/** Extract a YouTube embed URL from any standard YouTube link */
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url.trim());
    let videoId = null;
    if (u.hostname === 'youtu.be') {
      videoId = u.pathname.slice(1).split('?')[0];
    } else if (
      u.hostname === 'www.youtube.com' ||
      u.hostname === 'youtube.com' ||
      u.hostname === 'm.youtube.com'
    ) {
      if (u.pathname.startsWith('/embed/')) {
        return url.trim(); // already an embed URL
      }
      videoId = u.searchParams.get('v');
    }
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  } catch (_) {}
  return null;
}

const TABS = [
  { id: 'ad', label: 'Ad Popup', icon: <FaBullhorn />, desc: 'Modal' },
  { id: 'banner', label: 'Banner', icon: <FaMagic />, desc: 'Strip' },
  { id: 'video', label: 'Tutorial', icon: <FaYoutube />, desc: 'Video' },
];

const BANNER_SWATCHES = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#0ea5e9', '#111827'];

const SiteSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [activeTab, setActiveTab] = useState('ad');
  const fileInputRef = useRef(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiHelper.get('/admin/site-settings');
      setSettings({
        adEnabled: data.adEnabled ?? false,
        adType: data.adType ?? 'text',
        adTitle: data.adTitle ?? '',
        adDescription: data.adDescription ?? '',
        adMediaUrl: data.adMediaUrl ?? '',
        adMediaType: data.adMediaType ?? '',
        adButtonText: data.adButtonText ?? 'Learn More',
        adButtonUrl: data.adButtonUrl ?? '',
        youtubeUrl: data.youtubeUrl ?? '',
        bannerEnabled: data.bannerEnabled ?? false,
        bannerText: data.bannerText ?? '',
        bannerLink: data.bannerLink ?? '',
        bannerBgColor: data.bannerBgColor ?? '#6366f1',
        bannerTextColor: data.bannerTextColor ?? '#ffffff',
      });
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to load site settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await apiHelper.upload('/admin/site-settings/upload-media', formData);
      setSettings((prev) => ({
        ...prev,
        adMediaUrl: result.url,
        adMediaType: result.mediaType,
      }));
      showToast('success', 'Media uploaded successfully');
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to upload media');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveMedia = () => {
    setSettings((prev) => ({ ...prev, adMediaUrl: '', adMediaType: '' }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiHelper.put('/admin/site-settings', settings);
      showToast('success', 'Site settings saved successfully');
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to save site settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page site-settings-page ss-page">
        <div className="ss-loading">
          <div className="ss-loading-card">
            <div className="admin-spinner" />
            <h3>Loading site settings</h3>
            <p>Fetching your popup, banner &amp; video config…</p>
            <div className="ss-loading-bars">
              <span /><span /><span />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const embedUrl = getYouTubeEmbedUrl(settings.youtubeUrl);
  const adLive = settings.adEnabled;
  const bannerLive = settings.bannerEnabled;
  const videoLive = !!embedUrl;

  return (
    <div className="admin-page site-settings-page ss-page">
      {/* ── Hero header ─────────────────────────────── */}
      <header className="ss-hero">
        <div className="ss-hero-main">
          <div className="ss-hero-icon">
            <FaGlobe />
          </div>
          <div className="ss-hero-text">
            <div className="ss-hero-eyebrow">
              <FaEye /> Live on support site
            </div>
            <h1 className="ss-hero-title">Site Settings</h1>
            <p className="ss-hero-sub">
              Control the ad popup, footer banner and tutorial video visitors see.
            </p>
          </div>
        </div>
        <div className="ss-hero-side">
          <div className="ss-status-row">
            <span className={`ss-status ${adLive ? 'is-on' : 'is-off'}`}>
              <span className="ss-dot" />
              Ad {adLive ? 'On' : 'Off'}
            </span>
            <span className={`ss-status ${bannerLive ? 'is-on' : 'is-off'}`}>
              <span className="ss-dot" />
              Banner {bannerLive ? 'On' : 'Off'}
            </span>
            <span className={`ss-status ${videoLive ? 'is-on' : 'is-off'}`}>
              <span className="ss-dot" />
              Video {videoLive ? 'On' : 'Off'}
            </span>
          </div>
          <button className="ss-save-btn ss-save-btn--hero" onClick={handleSave} disabled={saving}>
            <FaSave /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </header>

      {/* ── Tab bar (best for mobile) ───────────────── */}
      <nav className="ss-tabs" role="tablist" aria-label="Site settings sections">
        {TABS.map((t) => {
          const isActive = activeTab === t.id;
          const badge =
            t.id === 'ad' ? adLive : t.id === 'banner' ? bannerLive : videoLive;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              className={`ss-tab ${isActive ? 'is-active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              <span className="ss-tab-icon">{t.icon}</span>
              <span className="ss-tab-text">
                <span className="ss-tab-label">{t.label}</span>
                <span className="ss-tab-sub">{t.desc}</span>
              </span>
              <span className={`ss-tab-live ${badge ? 'is-on' : ''}`} title={badge ? 'Live' : 'Hidden'}>
                <span className="ss-dot" />
              </span>
            </button>
          );
        })}
      </nav>

      {/* ── AD TAB ──────────────────────────────────── */}
      {activeTab === 'ad' && (
        <section className="ss-grid ss-grid--split">
          <div className="ss-card">
            <div className="ss-card-head">
              <div className="ss-card-icon ss-card-icon--indigo">
                <FaBullhorn />
              </div>
              <div className="ss-card-title-wrap">
                <h2>Advertisement popup</h2>
                <p>Shown to visitors on every page load when enabled.</p>
              </div>
              <button
                className={`ss-switch ${settings.adEnabled ? 'is-on' : ''}`}
                onClick={() => handleChange('adEnabled', !settings.adEnabled)}
                aria-pressed={settings.adEnabled}
                aria-label="Toggle advertisement"
              >
                <span className="ss-switch-thumb" />
                <span className="ss-switch-label">{settings.adEnabled ? 'On' : 'Off'}</span>
              </button>
            </div>

            <div className={`ss-card-body ${!settings.adEnabled ? 'is-disabled' : ''}`}>
              <div className="ss-field">
                <span className="ss-label">Format</span>
                <div className="ss-segmented">
                  <button
                    type="button"
                    className={`ss-seg ${settings.adType === 'media' ? 'is-active' : ''}`}
                    onClick={() => handleChange('adType', 'media')}
                    disabled={!settings.adEnabled}
                  >
                    <FaImage /> <span><strong>Media</strong><small>Image / video + CTA</small></span>
                  </button>
                  <button
                    type="button"
                    className={`ss-seg ${settings.adType === 'text' ? 'is-active' : ''}`}
                    onClick={() => handleChange('adType', 'text')}
                    disabled={!settings.adEnabled}
                  >
                    <FaFont /> <span><strong>Text</strong><small>Title + description</small></span>
                  </button>
                </div>
              </div>

              {settings.adType === 'media' && (
                <>
                  <div className="ss-field">
                    <span className="ss-label"><FaUpload /> Creative</span>
                    {settings.adMediaUrl ? (
                      <div className="ss-media-box">
                        {settings.adMediaType === 'video' ? (
                          <video src={settings.adMediaUrl} controls className="ss-media-el" />
                        ) : (
                          <img
                            src={settings.adMediaUrl}
                            alt="Ad creative"
                            className="ss-media-el"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div className="ss-media-bar">
                          <span className="ss-media-type">{settings.adMediaType || 'image'}</span>
                          <div className="ss-media-actions">
                            <button
                              type="button"
                              className="ss-chip-btn"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading || !settings.adEnabled}
                            >
                              <FaUpload /> Replace
                            </button>
                            <button
                              type="button"
                              className="ss-chip-btn ss-chip-btn--danger"
                              onClick={handleRemoveMedia}
                              disabled={!settings.adEnabled}
                            >
                              <FaTrash /> Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="ss-dropzone"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || !settings.adEnabled}
                      >
                        {uploading ? (
                          <><span className="admin-spinner-sm" /> Uploading…</>
                        ) : (
                          <>
                            <span className="ss-dropzone-icon"><FaUpload /></span>
                            <strong>Tap to upload image or video</strong>
                            <small>JPG · PNG · GIF · WebP · MP4 · MOV · WebM</small>
                          </>
                        )}
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*"
                      hidden
                      onChange={handleMediaUpload}
                    />
                  </div>
                  <div className="ss-row">
                    <label className="ss-field">
                      <span className="ss-label"><FaMousePointer /> Button text</span>
                      <input
                        type="text"
                        className="ss-input"
                        placeholder="Learn More"
                        value={settings.adButtonText}
                        onChange={(e) => handleChange('adButtonText', e.target.value)}
                        maxLength={40}
                        disabled={!settings.adEnabled}
                      />
                    </label>
                    <label className="ss-field">
                      <span className="ss-label"><FaLink /> Button URL <em>optional</em></span>
                      <input
                        type="url"
                        className="ss-input"
                        placeholder="https://example.com/offer"
                        value={settings.adButtonUrl}
                        onChange={(e) => handleChange('adButtonUrl', e.target.value)}
                        disabled={!settings.adEnabled}
                        inputMode="url"
                      />
                    </label>
                  </div>
                </>
              )}

              {settings.adType === 'text' && (
                <>
                  <label className="ss-field">
                    <span className="ss-label"><FaFont /> Headline</span>
                    <input
                      type="text"
                      className="ss-input"
                      placeholder="e.g. Special Offer — Limited Time!"
                      value={settings.adTitle}
                      onChange={(e) => handleChange('adTitle', e.target.value)}
                      maxLength={120}
                      disabled={!settings.adEnabled}
                    />
                    <span className="ss-count">{settings.adTitle.length}/120</span>
                  </label>
                  <label className="ss-field">
                    <span className="ss-label"><FaAlignLeft /> Description</span>
                    <textarea
                      className="ss-input ss-textarea"
                      placeholder="Short description shown below the headline…"
                      value={settings.adDescription}
                      onChange={(e) => handleChange('adDescription', e.target.value)}
                      maxLength={400}
                      rows={4}
                      disabled={!settings.adEnabled}
                    />
                    <span className="ss-count">{settings.adDescription.length}/400 · line breaks preserved</span>
                  </label>
                  <div className="ss-row">
                    <label className="ss-field">
                      <span className="ss-label"><FaMousePointer /> Button text</span>
                      <input
                        type="text"
                        className="ss-input"
                        placeholder="Learn More"
                        value={settings.adButtonText}
                        onChange={(e) => handleChange('adButtonText', e.target.value)}
                        maxLength={40}
                        disabled={!settings.adEnabled}
                      />
                    </label>
                    <label className="ss-field">
                      <span className="ss-label"><FaLink /> Button URL <em>optional</em></span>
                      <input
                        type="url"
                        className="ss-input"
                        placeholder="https://example.com/offer"
                        value={settings.adButtonUrl}
                        onChange={(e) => handleChange('adButtonUrl', e.target.value)}
                        disabled={!settings.adEnabled}
                        inputMode="url"
                      />
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Live preview */}
          <aside className="ss-preview-col">
            <div className="ss-preview-card">
              <div className="ss-preview-head">
                <span><FaMobileAlt /> Live preview</span>
                <span className={`ss-status mini ${settings.adEnabled ? 'is-on' : 'is-off'}`}>
                  <span className="ss-dot" /> {settings.adEnabled ? 'Visible' : 'Hidden'}
                </span>
              </div>
              {settings.adEnabled ? (
                <div className="ss-phone">
                  <div className="ss-phone-notch" />
                  <div className="ss-ad-modal">
                    {settings.adType === 'media' && settings.adMediaUrl ? (
                      settings.adMediaType === 'video' ? (
                        <video src={settings.adMediaUrl} className="ss-ad-media" muted loop autoPlay playsInline />
                      ) : (
                        <img
                          src={settings.adMediaUrl}
                          alt=""
                          className="ss-ad-media"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )
                    ) : settings.adType === 'media' ? (
                      <div className="ss-ad-empty"><FaImage /> No creative yet</div>
                    ) : null}
                    {settings.adType === 'text' && (
                      <div className="ss-ad-body">
                        <p className="ss-ad-title">{settings.adTitle || 'Your headline here'}</p>
                        <p className="ss-ad-desc" style={{ whiteSpace: 'pre-wrap' }}>
                          {settings.adDescription || 'Your short description will appear here…'}
                        </p>
                      </div>
                    )}
                    {(settings.adType === 'media' ? settings.adButtonUrl : true) && (
                      <div className={`ss-ad-cta-wrap ${settings.adType === 'media' ? 'is-centered' : ''}`}>
                        {settings.adButtonUrl ? (
                          <span className="ss-ad-cta">{settings.adButtonText || 'Learn More'}</span>
                        ) : settings.adType === 'text' ? (
                          <span className="ss-ad-cta ss-ad-cta--ghost">{settings.adButtonText || 'Learn More'} · add URL to activate</span>
                        ) : null}
                      </div>
                    )}
                  </div>
                  <p className="ss-preview-note">How it looks as a visitor popup on mobile.</p>
                </div>
              ) : (
                <div className="ss-preview-off">
                  <FaEye />
                  <p>Popup is turned off.<br />Toggle it on to preview.</p>
                </div>
              )}
            </div>
          </aside>
        </section>
      )}

      {/* ── BANNER TAB ──────────────────────────────── */}
      {activeTab === 'banner' && (
        <section className="ss-grid ss-grid--split">
          <div className="ss-card">
            <div className="ss-card-head">
              <div className="ss-card-icon ss-card-icon--green">
                <FaBullhorn />
              </div>
              <div className="ss-card-title-wrap">
                <h2>Footer banner strip</h2>
                <p>Slim announcement bar shown above the footer.</p>
              </div>
              <button
                className={`ss-switch ${settings.bannerEnabled ? 'is-on' : ''}`}
                onClick={() => handleChange('bannerEnabled', !settings.bannerEnabled)}
                aria-pressed={settings.bannerEnabled}
                aria-label="Toggle banner"
              >
                <span className="ss-switch-thumb" />
                <span className="ss-switch-label">{settings.bannerEnabled ? 'On' : 'Off'}</span>
              </button>
            </div>

            <div className={`ss-card-body ${!settings.bannerEnabled ? 'is-disabled' : ''}`}>
              <label className="ss-field">
                <span className="ss-label"><FaFont /> Banner message</span>
                <input
                  type="text"
                  className="ss-input"
                  placeholder="e.g. Special offer — Get 20% off Gold Plan!"
                  value={settings.bannerText}
                  onChange={(e) => handleChange('bannerText', e.target.value)}
                  maxLength={200}
                  disabled={!settings.bannerEnabled}
                />
                <span className="ss-count">{settings.bannerText.length}/200</span>
              </label>

              <label className="ss-field">
                <span className="ss-label"><FaLink /> Link URL <em>optional — tap banner to open</em></span>
                <input
                  type="url"
                  className="ss-input"
                  placeholder="https://example.com/offer"
                  value={settings.bannerLink}
                  onChange={(e) => handleChange('bannerLink', e.target.value)}
                  disabled={!settings.bannerEnabled}
                  inputMode="url"
                />
              </label>

              <div className="ss-row">
                <div className="ss-field">
                  <span className="ss-label"><FaPalette /> Background</span>
                  <div className="ss-color-row">
                    <label className="ss-color-well" style={{ background: settings.bannerBgColor }}>
                      <input
                        type="color"
                        value={settings.bannerBgColor}
                        onChange={(e) => handleChange('bannerBgColor', e.target.value)}
                        disabled={!settings.bannerEnabled}
                        aria-label="Banner background color"
                      />
                    </label>
                    <input
                      type="text"
                      className="ss-input ss-input--mono"
                      value={settings.bannerBgColor}
                      onChange={(e) => handleChange('bannerBgColor', e.target.value)}
                      maxLength={7}
                      disabled={!settings.bannerEnabled}
                      spellCheck={false}
                    />
                  </div>
                  <div className="ss-swatches">
                    {BANNER_SWATCHES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`ss-swatch ${settings.bannerBgColor.toLowerCase() === c ? 'is-active' : ''}`}
                        style={{ background: c }}
                        onClick={() => handleChange('bannerBgColor', c)}
                        disabled={!settings.bannerEnabled}
                        aria-label={`Use ${c}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="ss-field">
                  <span className="ss-label"><FaPalette /> Text color</span>
                  <div className="ss-color-row">
                    <label className="ss-color-well" style={{ background: settings.bannerTextColor }}>
                      <input
                        type="color"
                        value={settings.bannerTextColor}
                        onChange={(e) => handleChange('bannerTextColor', e.target.value)}
                        disabled={!settings.bannerEnabled}
                        aria-label="Banner text color"
                      />
                    </label>
                    <input
                      type="text"
                      className="ss-input ss-input--mono"
                      value={settings.bannerTextColor}
                      onChange={(e) => handleChange('bannerTextColor', e.target.value)}
                      maxLength={7}
                      disabled={!settings.bannerEnabled}
                      spellCheck={false}
                    />
                  </div>
                  <div className="ss-swatches">
                    {['#ffffff', '#111827', '#fef3c7', '#d1fae5'].map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`ss-swatch ss-swatch--light ${settings.bannerTextColor.toLowerCase() === c ? 'is-active' : ''}`}
                        style={{ background: c }}
                        onClick={() => handleChange('bannerTextColor', c)}
                        disabled={!settings.bannerEnabled}
                        aria-label={`Use ${c}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className="ss-preview-col">
            <div className="ss-preview-card">
              <div className="ss-preview-head">
                <span><FaEye /> Live preview</span>
                <span className={`ss-status mini ${settings.bannerEnabled && settings.bannerText ? 'is-on' : 'is-off'}`}>
                  <span className="ss-dot" /> {settings.bannerEnabled && settings.bannerText ? 'Visible' : 'Hidden'}
                </span>
              </div>
              {settings.bannerEnabled && settings.bannerText ? (
                <div className="ss-browser">
                  <div className="ss-browser-bar"><span /><span /><span /></div>
                  <div className="ss-browser-page">
                    <div className="ss-skel" /><div className="ss-skel short" />
                  </div>
                  <div
                    className="ss-banner-live"
                    style={{ backgroundColor: settings.bannerBgColor, color: settings.bannerTextColor }}
                  >
                    {settings.bannerLink ? (
                      <a href={settings.bannerLink} target="_blank" rel="noopener noreferrer" style={{ color: settings.bannerTextColor }}>
                        {settings.bannerText}
                      </a>
                    ) : settings.bannerText}
                  </div>
                  <p className="ss-preview-note">Pinned above the footer on the support site.</p>
                </div>
              ) : (
                <div className="ss-preview-off">
                  <FaBullhorn />
                  <p>{!settings.bannerEnabled ? 'Banner is turned off.' : 'Type a message to preview.'}</p>
                </div>
              )}
            </div>
          </aside>
        </section>
      )}

      {/* ── VIDEO TAB ───────────────────────────────── */}
      {activeTab === 'video' && (
        <section className="ss-grid ss-grid--split">
          <div className="ss-card">
            <div className="ss-card-head">
              <div className="ss-card-icon ss-card-icon--red">
                <FaYoutube />
              </div>
              <div className="ss-card-title-wrap">
                <h2>Tutorial video</h2>
                <p>Paste a YouTube link to show the tutorial section. Blank hides it.</p>
              </div>
              <span className={`ss-status ${embedUrl ? 'is-on' : 'is-off'}`}>
                <span className="ss-dot" /> {embedUrl ? 'Live' : 'Hidden'}
              </span>
            </div>

            <div className="ss-card-body">
              <label className="ss-field">
                <span className="ss-label"><FaLink /> YouTube URL</span>
                <input
                  type="url"
                  className="ss-input"
                  placeholder="https://www.youtube.com/watch?v=xxxxxxx  or  https://youtu.be/xxxxxxx"
                  value={settings.youtubeUrl}
                  onChange={(e) => handleChange('youtubeUrl', e.target.value)}
                  inputMode="url"
                />
                <span className="ss-hint">Supports watch, youtu.be short links &amp; embed URLs.</span>
              </label>

              {settings.youtubeUrl && !embedUrl && (
                <div className="ss-alert ss-alert--error">
                  <FaExclamationCircle /> Couldn&apos;t parse this as a YouTube URL. Use a standard watch or short link.
                </div>
              )}
              {embedUrl && (
                <div className="ss-alert ss-alert--success">
                  <FaCheckCircle /> Valid YouTube link — preview is live on the right.
                </div>
              )}

              <div className="ss-tip">
                <span className="ss-tip-icon"><FaPlay /></span>
                <div>
                  <strong>Tip for mobile viewers</strong>
                  <p>Keep videos under 3 minutes. The embed auto-scales to any screen size.</p>
                </div>
              </div>
            </div>
          </div>

          <aside className="ss-preview-col">
            <div className="ss-preview-card">
              <div className="ss-preview-head">
                <span><FaPlay /> Video preview</span>
                {settings.youtubeUrl && (
                  <button type="button" className="ss-chip-btn" onClick={() => handleChange('youtubeUrl', '')}>
                    <FaTrash /> Clear
                  </button>
                )}
              </div>
              {settings.youtubeUrl ? (
                embedUrl ? (
                  <div className="ss-yt">
                    <iframe
                      src={embedUrl}
                      title="YouTube preview"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="ss-preview-off">
                    <FaExclamationCircle />
                    <p>Invalid URL — fix the link to preview.</p>
                  </div>
                )
              ) : (
                <div className="ss-preview-off">
                  <FaYoutube />
                  <p>No video set.<br />Paste a link to preview it here.</p>
                </div>
              )}
            </div>
          </aside>
        </section>
      )}

      {/* ── Sticky save bar (thumb-friendly on mobile) ── */}
      <footer className="ss-savebar">
        <p className="ss-savebar-hint">
          <FaCheckCircle /> Changes aren&apos;t live until you save.
        </p>
        <button className="ss-save-btn" onClick={handleSave} disabled={saving}>
          <FaSave /> {saving ? 'Saving…' : 'Save changes'}
        </button>
      </footer>
    </div>
  );
};

export default SiteSettings;
